import { Container, Box, Flex, Pagination } from '@legion-ui/core';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmailBlastTable, { EmailBlast } from '@/components/EmailBlastTable';
import EmailBlastListHeader from '@/components/EmailBlastListHeader';
import EmailQuotaCard from '@/components/EmailQuotaCard';
import { useEmailBlastQuery, useEmailQuotaStatus } from '@/hooks/useEmailBlastQuery';

const EmailBlastListPage = () => {
  const navigate = useNavigate();

  // State management
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Fetch email blasts using the hook
  const { items, total, isLoading } = useEmailBlastQuery({
    page: currentPage,
    limit: pageSize,
    search: searchTerm,
  });

  // Fetch email quota status
  const { data: quotaStatus, isLoading: isQuotaLoading, isError, error } = useEmailQuotaStatus();

  // Debug logs
  console.log('quotaStatus:', quotaStatus);
  console.log('isQuotaLoading:', isQuotaLoading);
  console.log('isError:', isError);
  console.log('remaining value:', quotaStatus?.remaining);

  // Search handler
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Pagination
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handlePageSizeChange = (dataSizeOption: number) => {
    setPageSize(dataSizeOption);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleCreateEmail = () => {
    // Navigate to create email page or open modal
    navigate('/email-blast/create');
  };

  const handleViewEmail = (emailBlast: EmailBlast) => {
    // Navigate to email detail page
    navigate(`/email-blast/view/${emailBlast.id}`);
  };

  return (
    <article>
      <Container>
        <Box as="main">
          {/* Header */}
          <Box as="header" mb="1rem">
            <h1 className="text-2xl font-bold text-gray-900">Bulk Email</h1>
            <p className="mt-2 text-gray-600">Manage Sent email notification</p>
          </Box>

          {/* Email Quota Card */}
          <EmailQuotaCard
            remaining={quotaStatus?.remaining}
            isLoading={isQuotaLoading}
          />

          {/* Search and Create Button Row */}
          <EmailBlastListHeader
            searchTerm={searchTerm}
            onSearchChange={handleSearch}
            onCreateClick={handleCreateEmail}
          />

          {/* Email Blast Table */}
          <EmailBlastTable
            emailBlasts={items}
            isLoading={isLoading}
            onView={handleViewEmail}
            currentPage={currentPage}
            itemsPerPage={pageSize}
          />

          {/* Pagination */}
          {!isLoading && total > 0 && (
            <Box mt="1rem">
              <Flex alignX="flex-end">
                <Pagination
                  key={`pagination-${currentPage}-${pageSize}`}
                  currentPage={currentPage}
                  totalPages={total}
                  onClickPage={handlePageChange}
                  variant="rounded"
                  onClickOption={handlePageSizeChange}
                  options={[
                    pageSize.toString(),
                    ...['10', '20', '30', '40', '50'].filter(opt => opt !== pageSize.toString())
                  ]}
                />
              </Flex>
            </Box>
          )}
        </Box>
      </Container>
    </article>
  );
};

export default EmailBlastListPage;
